/**
 * Emerald Books — API client + auth context.
 *
 * Isolated from the Emerald frontend API layer so the whole `books/` slice can
 * be extracted into its own repo later. Uses a dedicated JWT stored under
 * `books_token` so it doesn't conflict with the Emerald `token`.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const BOOKS_API = `${BACKEND_URL}/api/books`;

/**
 * Turn any axios error into a human-readable string. Handles FastAPI's 422
 * `detail` array (list of {loc, msg, ...} objects) so validation errors
 * never crash React by being rendered as objects.
 */
export const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => {
      const field = Array.isArray(x?.loc) ? x.loc.slice(-1)[0] : '';
      return field ? `${field}: ${x.msg}` : (x?.msg || 'Invalid');
    }).join(' · ');
  }
  if (d && typeof d === 'object') return JSON.stringify(d);
  return err?.message || 'Something went wrong';
};

/** Strip empty-string values from a plain object so optional string fields
 *  aren't sent as "" (blocks EmailStr / non-Optional str validators). */
export const stripEmpty = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === '' || v === undefined) continue;
    out[k] = v;
  }
  return out;
};

export const booksClient = axios.create({ baseURL: BOOKS_API });

/**
 * Some Books list endpoints return `{data: [...], count: n}` (contacts, ledger
 * accounts, journals) while newer Sprint-3 endpoints return bare arrays
 * (payroll, inventory, pre-docs, budgets, branches). Use this helper on any
 * `axios` response whose body could be either shape.
 */
export const unwrapList = (resp) => {
  const body = resp?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

booksClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('books_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

booksClient.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // Force re-login on any auth failure
      localStorage.removeItem('books_token');
      const path = window.location.pathname;
      const public_paths = ['/books', '/books/login', '/books/signup', '/books/pricing',
                            '/books/forgot-password', '/books/reset-password'];
      if (!public_paths.some((p) => path === p || path.startsWith(p + '?'))) {
        window.location.href = '/books/login';
      }
    }
    return Promise.reject(err);
  },
);

// --------- Auth ---------
export const booksSignup = async (payload) => (await booksClient.post('/auth/signup', payload)).data;
export const booksLogin = async (email, password, tenant_slug) =>
  (await booksClient.post('/auth/login', { email, password, tenant_slug })).data;
export const booksMe = async () => (await booksClient.get('/auth/me')).data;
export const booksInvite = async (payload) => (await booksClient.post('/auth/invite', payload)).data;
export const booksSetPassword = async (token, password) =>
  (await booksClient.post('/auth/set-password', { token, password })).data;
export const booksForgotPassword = async (email) =>
  (await booksClient.post('/auth/forgot-password', { email })).data;
export const booksResetPassword = async (token, password) =>
  (await booksClient.post('/auth/reset-password', { token, password })).data;

// --------- Billing ---------
export const listPlans = async () => (await booksClient.get('/billing/plans')).data;
export const myBilling = async () => (await booksClient.get('/billing/me')).data;
export const startCheckout = async (payload) => (await booksClient.post('/billing/checkout', payload)).data;
export const billingPortal = async (return_url) =>
  (await booksClient.post('/billing/portal', { return_url })).data;
export const checkoutStatus = async (session_id) =>
  (await booksClient.get(`/billing/status/${session_id}`)).data;

// --------- Attachments ---------
export const uploadAttachment = async (file, kind, parent_id) => {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  if (parent_id) form.append('parent_id', parent_id);
  const { data } = await booksClient.post('/attachments', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};
export const listAttachments = async (params) => (await booksClient.get('/attachments', { params })).data;
export const deleteAttachment = async (id) => (await booksClient.delete(`/attachments/${id}`)).data;
export const attachmentDownloadUrl = (id) => `${BOOKS_API}/attachments/${id}/download`;

// --------- Masters ---------
export const listBooksCountries = async () => (await booksClient.get('/masters/countries')).data;
export const listBooksCurrencies = async () => (await booksClient.get('/masters/currencies')).data;
export const listBooksTaxCodes = async (country_code) =>
  (await booksClient.get('/masters/tax-codes', { params: country_code ? { country_code } : {} })).data;
export const listBooksCoaTemplates = async (country_code) =>
  (await booksClient.get('/masters/coa-templates', { params: country_code ? { country_code } : {} })).data;

// --------- Ledger ---------
export const listAccounts = async (params) => (await booksClient.get('/ledger/accounts', { params })).data;
export const createAccount = async (p) => (await booksClient.post('/ledger/accounts', p)).data;
export const updateAccount = async (id, p) => (await booksClient.patch(`/ledger/accounts/${id}`, p)).data;

export const listJournals = async (params) => (await booksClient.get('/ledger/journals', { params })).data;
export const createJournal = async (p) => (await booksClient.post('/ledger/journals', p)).data;
export const getJournal = async (id) => (await booksClient.get(`/ledger/journals/${id}`)).data;
export const reverseJournal = async (id) => (await booksClient.post(`/ledger/journals/${id}/reverse`)).data;

// --------- Reports ---------
export const reportTB = async (params) => (await booksClient.get('/ledger/reports/trial-balance', { params })).data;
export const reportPL = async (params) => (await booksClient.get('/ledger/reports/profit-loss', { params })).data;
export const reportBS = async (params) => (await booksClient.get('/ledger/reports/balance-sheet', { params })).data;
export const reportCF = async (params) => (await booksClient.get('/ledger/reports/cash-flow', { params })).data;
export const reportDashboard = async () => (await booksClient.get('/ledger/reports/dashboard')).data;

// --------- FX (Phase 4) ---------
export const listFxRates = async (params) => (await booksClient.get('/fx', { params })).data;
export const createFxRate = async (p) => (await booksClient.post('/fx', p)).data;
export const fetchFxRates = async (p) => (await booksClient.post('/fx/fetch', p || {})).data;
export const lookupFxRate = async (params) => (await booksClient.get('/fx/lookup', { params })).data;
export const revalueFx = async (params) => (await booksClient.post('/fx/revalue', null, { params })).data;
export const listRevaluations = async () => (await booksClient.get('/fx/revaluations')).data;

// --------- Fixed Assets (Phase 4) ---------
export const listAssets = async (params) => (await booksClient.get('/fixed-assets', { params })).data;
export const createAsset = async (p) => (await booksClient.post('/fixed-assets', p)).data;
export const depreciateAsset = async (id, p) => (await booksClient.post(`/fixed-assets/${id}/depreciate`, p)).data;
export const depreciateAssetsBatch = async (p) => (await booksClient.post('/fixed-assets/depreciate-batch', p)).data;
export const disposeAsset = async (id, p) => (await booksClient.post(`/fixed-assets/${id}/dispose`, p)).data;
export const assetSchedule = async (id) => (await booksClient.get(`/fixed-assets/${id}/schedule`)).data;

// --------- Session helpers ---------
export const saveBooksSession = (token, user, tenant) => {
  localStorage.setItem('books_token', token);
  if (user) localStorage.setItem('books_user', JSON.stringify(user));
  if (tenant) localStorage.setItem('books_tenant', JSON.stringify(tenant));
};
export const clearBooksSession = () => {
  localStorage.removeItem('books_token');
  localStorage.removeItem('books_user');
  localStorage.removeItem('books_tenant');
};
export const getBooksUser = () => {
  try { return JSON.parse(localStorage.getItem('books_user') || 'null'); } catch { return null; }
};
export const getBooksTenant = () => {
  try { return JSON.parse(localStorage.getItem('books_tenant') || 'null'); } catch { return null; }
};
